"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { LogsApiResponse } from "@/types/api";
import type { PaginationAnchor, SortDirection } from "@/types/filters";
import type { UrlFilterState } from "./use-url-filters";
import { apiFetch, buildQueryString, isAbortError, ApiClientError, type QueryParamValue } from "@/lib/api/api-client";
import { useLocalStorage } from "./use-local-storage";
import { LOCAL_STORAGE_KEYS } from "@/lib/constants";
import { ALLOWED_PAGE_SIZES, type AllowedPageSize } from "@/types/filters";
import { canWalkToPage, shouldFetchFromEnd } from "@/utils/pagination";

function filtersKey(filters: UrlFilterState, pageSize: number): string {
  return JSON.stringify({
    ...filters,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    pageSize,
  });
}

function flipSortDirection(direction: SortDirection | undefined): SortDirection {
  return direction === "asc" ? "desc" : "asc";
}

function buildLogsQuery(
  filters: UrlFilterState,
  pageSize: number,
  options: {
    cursor?: string;
    page?: number;
    totalCount?: number;
    reverseSort?: boolean;
  } = {},
): string {
  const sortDirection = options.reverseSort
    ? flipSortDirection(filters.sortDirection)
    : filters.sortDirection;

  const params: Record<string, QueryParamValue> = {
    userId: filters.userIds,
    route: filters.routes,
    method: filters.methods,
    status: filters.errorStatuses as string[] | undefined,
    statusCode: filters.statusCodes,
    responseStored: filters.responseStored,
    fixedStatus: filters.fixedStatus,
    createdFrom: filters.createdFrom?.toISOString(),
    createdTo: filters.createdTo?.toISOString(),
    updatedFrom: filters.updatedFrom?.toISOString(),
    updatedTo: filters.updatedTo?.toISOString(),
    responseTimeMinMs: filters.responseTimeMinMs,
    responseTimeMaxMs: filters.responseTimeMaxMs,
    search: filters.search,
    sortBy: filters.sortBy,
    sortDirection,
    routeGroupingEnabled: true,
    lookback: filters.lookbackAll ? "all" : undefined,
    pageSize,
    cursor: options.cursor,
    page: options.page,
    totalCount: options.totalCount,
  };
  return buildQueryString(params);
}

function normalizePageSize(size: number): AllowedPageSize {
  if ((ALLOWED_PAGE_SIZES as readonly number[]).includes(size)) {
    return size as AllowedPageSize;
  }
  return 50;
}

export interface LogQueryJumpContext {
  totalCount: number;
  totalPages: number;
  canJumpToEnd: boolean;
}

export interface UseLogQueryResult {
  data: LogsApiResponse | null;
  isLoading: boolean;
  error: ApiClientError | null;
  refetch: () => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (page: number) => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  currentPage: number;
  knownPageCount: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  paginationAnchor: PaginationAnchor;
}

export function useLogQuery(
  filters: UrlFilterState,
  jumpContext: LogQueryJumpContext | null = null,
): UseLogQueryResult {
  const [storedPageSize, setStoredPageSize] = useLocalStorage<number>(LOCAL_STORAGE_KEYS.pageSize, 50);
  const pageSize = normalizePageSize(storedPageSize);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<PaginationAnchor>("start");
  const [data, setData] = useState<LogsApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const key = filtersKey(filters, pageSize);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const jumpContextRef = useRef(jumpContext);
  jumpContextRef.current = jumpContext;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const dataRef = useRef(data);
  dataRef.current = data;
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const cursorStackRef = useRef(cursorStack);
  cursorStackRef.current = cursorStack;
  const currentPageRef = useRef(1);

  const [paginationKey, setPaginationKey] = useState(key);
  if (key !== paginationKey) {
    setPaginationKey(key);
    if (cursorStack.length > 0) setCursorStack([]);
    if (anchor !== "start") setAnchor("start");
  }

  const currentCursor = cursorStack[cursorStack.length - 1];
  const previousKeyRef = useRef(key);
  /** After a direct jump, skip the redundant cursor-stack effect fetch. */
  const suppressCursorFetchRef = useRef(false);

  useEffect(() => {
    if (suppressCursorFetchRef.current) {
      suppressCursorFetchRef.current = false;
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    const filtersChanged = previousKeyRef.current !== key;
    previousKeyRef.current = key;
    if (filtersChanged) {
      setData(null);
    }

    const reverseSort = anchor === "end";
    apiFetch<LogsApiResponse>(
      `/api/logs${buildLogsQuery(filters, pageSize, { cursor: currentCursor, reverseSort })}`,
      { signal: controller.signal },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        const normalized =
          reverseSort
            ? { ...response, records: response.records.slice().reverse() }
            : response;
        setData(normalized);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (isAbortError(err) || controller.signal.aborted) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        setIsLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, currentCursor, refetchToken, anchor]);

  const totalPages = Math.max(1, jumpContext?.totalPages ?? 1);

  const currentPage =
    anchor === "end" && jumpContext?.canJumpToEnd
      ? Math.max(1, totalPages - cursorStack.length)
      : cursorStack.length + 1;
  currentPageRef.current = currentPage;

  const jumpToPageRequest = useCallback((page: number) => {
    const ctx = jumpContextRef.current;
    const pages = Math.max(1, ctx?.totalPages ?? 1);
    if (page < 1 || page > pages) return;
    if (!canWalkToPage(page, pages)) return;
    if (shouldFetchFromEnd(page, pages) && !ctx?.canJumpToEnd) return;

    setIsLoading(true);
    setError(null);

    apiFetch<LogsApiResponse>(
      `/api/logs${buildLogsQuery(filtersRef.current, pageSizeRef.current, {
        page,
        totalCount: ctx?.canJumpToEnd ? ctx.totalCount : undefined,
      })}`,
    )
      .then((response) => {
        const nextAnchor = response.pagination.anchor ?? "start";
        const nextStack = response.pagination.cursorStack ?? [];
        suppressCursorFetchRef.current = true;
        setAnchor(nextAnchor);
        setCursorStack(nextStack);
        setData(response);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (isAbortError(err)) return;
        setError(err instanceof ApiClientError ? err : new ApiClientError(500, "INTERNAL_ERROR", "Request failed"));
        setIsLoading(false);
      });
  }, []);

  const goToNextPage = useCallback(() => {
    const pages = Math.max(1, jumpContextRef.current?.totalPages ?? currentPageRef.current + 1);
    const current = currentPageRef.current;

    if (anchorRef.current === "end") {
      if (cursorStackRef.current.length > 0) {
        setCursorStack((stack) => stack.slice(0, -1));
        return;
      }
      // Already at last page.
      return;
    }

    const payload = dataRef.current;
    if (payload?.pagination.hasMore && payload.pagination.nextCursor) {
      setCursorStack((stack) => [...stack, payload.pagination.nextCursor as string]);
      return;
    }

    // No cursor (e.g. after a jump that omitted hasMore) — re-walk to current+1.
    if (current < pages && canWalkToPage(current + 1, pages)) {
      jumpToPageRequest(current + 1);
    }
  }, [jumpToPageRequest]);

  const goToPreviousPage = useCallback(() => {
    const pages = Math.max(1, jumpContextRef.current?.totalPages ?? currentPageRef.current);
    const current = currentPageRef.current;

    if (anchorRef.current === "end") {
      const payload = dataRef.current;
      if (payload?.pagination.hasMore && payload.pagination.nextCursor) {
        setCursorStack((stack) => [...stack, payload.pagination.nextCursor as string]);
        return;
      }
      // No reverse cursor — re-walk from the end to current-1 (e.g. 4544 → 4543).
      if (current > 1 && canWalkToPage(current - 1, pages)) {
        jumpToPageRequest(current - 1);
      }
      return;
    }

    if (cursorStackRef.current.length > 0) {
      setCursorStack((stack) => stack.slice(0, -1));
      return;
    }

    if (current > 1 && canWalkToPage(current - 1, pages)) {
      jumpToPageRequest(current - 1);
    }
  }, [jumpToPageRequest]);

  const goToPage = useCallback(
    (page: number) => {
      if (!Number.isFinite(page) || page < 1) return;

      const ctx = jumpContextRef.current;
      const pages = Math.max(1, ctx?.totalPages ?? 1);
      const currentAnchor = anchorRef.current;
      const stack = cursorStackRef.current;
      const current =
        currentAnchor === "end" && ctx?.canJumpToEnd
          ? Math.max(1, pages - stack.length)
          : stack.length + 1;

      if (page === current) return;

      // Move within the existing start stack (backward).
      if (currentAnchor === "start" && page < current && page >= 1) {
        setCursorStack((s) => s.slice(0, page - 1));
        return;
      }

      // Move within the existing end stack toward the last page.
      if (currentAnchor === "end") {
        const targetDepth = pages - page + 1;
        const currentDepth = stack.length + 1;
        if (targetDepth >= 1 && targetDepth < currentDepth) {
          setCursorStack((s) => s.slice(0, targetDepth - 1));
          return;
        }
        if (
          targetDepth === currentDepth + 1 &&
          dataRef.current?.pagination.hasMore &&
          dataRef.current.pagination.nextCursor
        ) {
          setCursorStack((s) => [...s, dataRef.current!.pagination.nextCursor as string]);
          return;
        }
      }

      // Sequential ±1 via cursor helpers (includes walk fallback).
      if (page === current + 1) {
        goToNextPage();
        return;
      }
      if (page === current - 1) {
        goToPreviousPage();
        return;
      }

      if (!canWalkToPage(page, pages)) return;
      if (shouldFetchFromEnd(page, pages) && !ctx?.canJumpToEnd) return;

      jumpToPageRequest(page);
    },
    [goToNextPage, goToPreviousPage, jumpToPageRequest],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setStoredPageSize(normalizePageSize(size));
      setCursorStack([]);
      setAnchor("start");
    },
    [setStoredPageSize],
  );

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  const cursorHasNext =
    anchor === "end"
      ? cursorStack.length > 0
      : Boolean(data?.pagination.hasMore && data?.pagination.nextCursor);

  const cursorHasPrev =
    anchor === "end"
      ? Boolean(data?.pagination.hasMore && data?.pagination.nextCursor)
      : cursorStack.length > 0;

  // Cursor continuation works even before totals load; walk fallback needs totals.
  const hasNextPage =
    cursorHasNext ||
    (Boolean(jumpContext?.canJumpToEnd) &&
      currentPage < totalPages &&
      canWalkToPage(currentPage + 1, totalPages));

  const hasPreviousPage =
    cursorHasPrev ||
    (currentPage > 1 &&
      Boolean(jumpContext?.canJumpToEnd) &&
      canWalkToPage(currentPage - 1, totalPages));

  return {
    data,
    isLoading,
    error,
    refetch,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    hasPreviousPage,
    hasNextPage,
    currentPage,
    knownPageCount: Math.max(currentPage, jumpContext?.totalPages ?? currentPage),
    pageSize,
    setPageSize,
    paginationAnchor: anchor,
  };
}
